import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import AdminLayout from '../components/AdminLayout';
import { configService, AppConfig, BoothConfig, HourlyPackage, ContactInfo } from '../services/ConfigService';
import RiyalSymbol from '../components/RiyalSymbol';
import { BoothPricingEditor } from '../components/BoothPricingEditor';
import '../styles/PagesManagement.css';
import '../styles/Admin.css';

function PagesManagement() {
  const navigate = useNavigate();
  const [config, setConfig] = useState<AppConfig>(configService.getConfig());
  const [activeTab, setActiveTab] = useState<'booths' | 'pricing' | 'features' | 'contact'>('booths');
  const [isSaving, setIsSaving] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadLatestConfig = async () => {
      try {
        setLoading(true);
        await configService.reloadConfig();
        setConfig(configService.getConfig());
      } catch (error) {
        console.error('❌ Error reloading configuration:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadLatestConfig();
  }, []);

  const handleBoothUpdate = (index: number, updates: Partial<BoothConfig>) => {
    const updatedConfig = { ...config };
    updatedConfig.booths[index] = { ...updatedConfig.booths[index], ...updates };
    setConfig(updatedConfig);
    setHasUnsavedChanges(true);
  };

  const handleAddBooth = () => {
    const updatedConfig = { ...config };
    updatedConfig.booths.push({
      name: `كابينة ${updatedConfig.booths.length + 1}`,
      slug: `booth${updatedConfig.booths.length + 1}`,
      description: 'وصف الكابينة الجديدة',
      imageUrl: '/gala-booth.png',
      dimensions: { height: 200, width: 150, depth: 150 },
      customPricing: {
        enabled: false,
        hourlyPackages: [],
        ticketSystem: { enabled: true, installationFee: 2500, freeTickets: 15, ticketPrice: 35 }
      }
    });
    setConfig(updatedConfig);
    setHasUnsavedChanges(true);
  };

  const handleRemoveBooth = (index: number) => {
    const updatedConfig = { ...config };
    updatedConfig.booths.splice(index, 1);
    setConfig(updatedConfig);
    setHasUnsavedChanges(true);
  };

  const handleHourlyPackageUpdate = (index: number, updates: Partial<HourlyPackage>) => {
    const updatedConfig = { ...config };
    updatedConfig.hourlyPackages[index] = { ...updatedConfig.hourlyPackages[index], ...updates };
    setConfig(updatedConfig);
    setHasUnsavedChanges(true);
  };

  const addFeature = (systemType: 'hourlyFeatures' | 'ticketFeatures') => {
    const updatedConfig = { ...config };
    updatedConfig.features[systemType].push('ميزة جديدة');
    setConfig(updatedConfig);
    setHasUnsavedChanges(true);
  };

  const updateFeature = (systemType: 'hourlyFeatures' | 'ticketFeatures', index: number, value: string) => {
    const updatedConfig = { ...config };
    updatedConfig.features[systemType][index] = value;
    setConfig(updatedConfig);
    setHasUnsavedChanges(true);
  };

  const removeFeature = (systemType: 'hourlyFeatures' | 'ticketFeatures', index: number) => {
    const updatedConfig = { ...config };
    updatedConfig.features[systemType].splice(index, 1);
    setConfig(updatedConfig);
    setHasUnsavedChanges(true);
  };

  const handleContactUpdate = (updates: Partial<ContactInfo>) => {
    const updatedConfig = { ...config };
    updatedConfig.contactInfo = { ...updatedConfig.contactInfo, ...updates };
    setConfig(updatedConfig);
    setHasUnsavedChanges(true);
  };

  const handleTicketSystemUpdate = (updates: any) => {
    const newConfig = { ...config };
    newConfig.ticketSystem = { ...newConfig.ticketSystem, ...updates };
    setConfig(newConfig);
    setHasUnsavedChanges(true);
  };

  const handleFeatureUpdate = (type: 'hourlyFeatures' | 'ticketFeatures', index: number, value: string) => {
    const newConfig = { ...config };
    newConfig.features[type][index] = value;
    setConfig(newConfig);
    setHasUnsavedChanges(true);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await configService.saveConfig(config);
      setHasUnsavedChanges(false);
      setSuccessMessage('تم حفظ التغييرات بنجاح في قاعدة البيانات!');
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 5000);
    } catch (error) {
      console.error('Error saving config:', error);
      setSuccessMessage('تم حفظ التغييرات محلياً، لكن فشل حفظها في قاعدة البيانات');
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 5000);
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = async () => {
    if (window.confirm('هل أنت متأكد من إعادة الإعدادات إلى القيم الافتراضية؟')) {
      setIsSaving(true);
      try {
        await configService.resetToDefault();
        setConfig(configService.getConfig());
        setHasUnsavedChanges(false);
        setSuccessMessage('تم إعادة تعيين الإعدادات بنجاح في قاعدة البيانات!');
        setShowSuccess(true);
      } catch (error) {
        console.error('Error resetting config:', error);
        setSuccessMessage('فشل في إعادة تعيين الإعدادات في قاعدة البيانات');
        setShowSuccess(true);
      } finally {
        setIsSaving(false);
      }
      setTimeout(() => setShowSuccess(false), 5000);
    }
  };

  return (
    <AdminLayout title="إدارة الإعدادات">
      <motion.div 
        className="settings-overview"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="section-header">
          <h1>إدارة الصفحات</h1>
          <p>تحرير محتوى وإعدادات الموقع</p>
        </div>

        <div className="tabs-container">
          <div className="tabs-nav">
            <button
              className={`tab-btn ${activeTab === 'booths' ? 'active' : ''}`}
              onClick={() => setActiveTab('booths')}
            >
              الكابينات
            </button>
            <button
              className={`tab-btn ${activeTab === 'pricing' ? 'active' : ''}`}
              onClick={() => setActiveTab('pricing')}
            >
              الأسعار
            </button>
            <button
              className={`tab-btn ${activeTab === 'features' ? 'active' : ''}`}
              onClick={() => setActiveTab('features')}
            >
              المميزات
            </button>
            <button
              className={`tab-btn ${activeTab === 'contact' ? 'active' : ''}`}
              onClick={() => setActiveTab('contact')}
            >
              التواصل
            </button>
          </div>
        </div>

        {/* Booths Tab */}
        {!loading && activeTab === 'booths' && (
          <div className="booths-section">
            <div className="section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <div className="section-description">
                <h2>إدارة الكبائن</h2>
                <p>قم بتعديل تفاصيل الكبائن المتوفرة للحجز</p>
              </div>
              <button 
                className="save-btn" 
                onClick={handleAddBooth}
                style={{ backgroundColor: '#4caf50', display: 'flex', gap: '8px', alignItems: 'center' }}
              >
                <span>+</span> إضافة كابينة جديدة
              </button>
            </div>
            
            {config.booths.map((booth, index) => (
              <div key={index} className="booth-config">
                <div className="config-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <h3>{booth.name}</h3>
                  <button 
                    onClick={() => handleRemoveBooth(index)}
                    style={{ background: 'none', border: 'none', color: '#e74c3c', cursor: 'pointer', fontSize: '14px', padding: '5px' }}
                    title="حذف الكابينة"
                  >
                    🗑️ حذف
                  </button>
                </div>
                <div className="config-fields">
                    <div className="field-group">
                      <label>
                        اسم الكابينة
                        <div className="help-tooltip">
                          <div className="help-icon">؟</div>
                          <div className="help-content">اسم مميز للكابينة يظهر للعملاء</div>
                        </div>
                    </label>
                    <input
                      type="text"
                      value={booth.name}
                      onChange={(e) => handleBoothUpdate(index, { name: e.target.value })}
                      placeholder="مثال: كابينة الألوان المبهجة"
                    />
                  </div>
                  
                  <div className="field-group">
                    <label>
                      الرابط (Slug)
                      <div className="help-tooltip">
                        <div className="help-icon">؟</div>
                        <div className="help-content">الاسم الإنجليزي القصير الذي سيظهر في رابط الصفحة (مثال: gala)</div>
                      </div>
                    </label>
                    <input
                      type="text"
                      value={booth.slug}
                      onChange={(e) => handleBoothUpdate(index, { slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                      placeholder="مثال: gala"
                      dir="ltr"
                    />
                  </div>

                  <div className="field-group">
                    <label>
                      رابط الصورة
                      <div className="help-tooltip">
                        <div className="help-icon">؟</div>
                        <div className="help-content">مسار أو رابط الصورة الخاصة بالكابينة</div>
                      </div>
                    </label>
                    <input
                      type="text"
                      value={booth.imageUrl || ''}
                      onChange={(e) => handleBoothUpdate(index, { imageUrl: e.target.value })}
                      placeholder="مثال: /images/Booth-Classic.png أو رابط مباشر"
                    />
                  </div>

                  <div className="field-group">
                    <label>
                      الوصف
                      <div className="help-tooltip">
                        <div className="help-icon">؟</div>
                        <div className="help-content">وصف تفصيلي لمحتويات الكابينة ومميزاتها</div>
                      </div>
                    </label>
                    <textarea
                      value={booth.description}
                      onChange={(e) => handleBoothUpdate(index, { description: e.target.value })}
                      rows={3}
                      placeholder="مثال: كابينة مجهزة بإضاءة احترافية وخلفيات متنوعة..."
                    />
                  </div>
                  
                  <div className="dimensions-fields">
                    <h4>المقاسات (سم)</h4>
                    <div className="field-group-row">
                      <div className="field-group">
                        <label>الارتفاع</label>
                        <input
                          type="number"
                          value={booth.dimensions?.height || ''}
                          onChange={(e) => handleBoothUpdate(index, { 
                            dimensions: {
                              ...booth.dimensions,
                              height: parseInt(e.target.value) || 0,
                              width: booth.dimensions?.width || 0,
                              depth: booth.dimensions?.depth || 0
                            }
                          })}
                          min="0"
                        />
                      </div>
                      
                      <div className="field-group">
                        <label>العرض</label>
                        <input
                          type="number"
                          value={booth.dimensions?.width || ''}
                          onChange={(e) => handleBoothUpdate(index, { 
                            dimensions: {
                              height: booth.dimensions?.height || 0,
                              width: parseInt(e.target.value) || 0,
                              depth: booth.dimensions?.depth || 0
                            }
                          })}
                          min="0"
                        />
                      </div>
                      
                      <div className="field-group">
                        <label>العمق</label>
                        <input
                          type="number"
                          value={booth.dimensions?.depth || ''}
                          onChange={(e) => handleBoothUpdate(index, { 
                            dimensions: {
                              height: booth.dimensions?.height || 0,
                              width: booth.dimensions?.width || 0,
                              depth: parseInt(e.target.value) || 0
                            }
                          })}
                          min="0"
                        />
                      </div>
                    </div>
                  </div>
                  <BoothPricingEditor 
                    booth={booth} 
                    onUpdate={(customPricing) => handleBoothUpdate(index, { customPricing })} 
                  />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pricing Tab */}
        {activeTab === 'pricing' && (
          <div className="pricing-section">
            <div className="section-description">
              <strong>💰 إدارة الأسعار:</strong> يمكنك تفعيل أو إلغاء أي من أنظمة التسعير. النظام المُفعّل سيظهر للعملاء في الموقع.
            </div>
            <div className="pricing-group">
              <div className="system-header">
                <h2>إعدادات العربون</h2>
              </div>
              <div className="package-config">
                <div className="config-fields horizontal">
                  <div className="field-group">
                    <label>تفعيل العربون</label>
                    <label className="switch">
                      <input
                        type="checkbox"
                        checked={config.depositSettings?.enabled || false}
                        onChange={(e) => setConfig({ ...config, depositSettings: { enabled: e.target.checked, type: config.depositSettings?.type || 'percentage', amount: config.depositSettings?.amount || 100, percentage: config.depositSettings?.percentage || 50, autoReminder: config.depositSettings?.autoReminder || false } })}
                      />
                      <span className="slider"></span>
                    </label>
                  </div>
                  <div className="field-group">
                    <label>نوع العربون</label>
                    <select
                      value={config.depositSettings?.type || 'percentage'}
                      onChange={(e) => setConfig({ ...config, depositSettings: { enabled: config.depositSettings?.enabled || false, percentage: config.depositSettings?.percentage || 50, autoReminder: config.depositSettings?.autoReminder || false, amount: config.depositSettings?.amount || 100, type: e.target.value as 'percentage' | 'fixed' } })}
                      className="settings-select"
                      style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                    >
                      <option value="percentage">نسبة مئوية (%)</option>
                      <option value="fixed">مبلغ ثابت (ريال)</option>
                    </select>
                  </div>
                  {config.depositSettings?.type === 'fixed' ? (
                    <div className="field-group">
                      <label>مبلغ العربون (ريال)</label>
                      <input
                        type="number"
                        value={config.depositSettings?.amount || 100}
                        onChange={(e) => setConfig({ ...config, depositSettings: { enabled: config.depositSettings?.enabled || false, type: config.depositSettings?.type || 'percentage', percentage: config.depositSettings?.percentage || 50, autoReminder: config.depositSettings?.autoReminder || false, amount: parseInt(e.target.value) || 0 } })}
                        min="0"
                      />
                    </div>
                  ) : (
                    <div className="field-group">
                      <label>نسبة العربون (%)</label>
                      <input
                        type="number"
                        value={config.depositSettings?.percentage || 50}
                        onChange={(e) => setConfig({ ...config, depositSettings: { enabled: config.depositSettings?.enabled || false, type: config.depositSettings?.type || 'percentage', autoReminder: config.depositSettings?.autoReminder || false, amount: config.depositSettings?.amount || 100, percentage: parseInt(e.target.value) || 0 } })}
                        min="0"
                        max="100"
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="pricing-group">
              <div className="system-header">
                <h2>
                  نظام الساعات
                  <span className="info-badge">حسب المدة</span>
                </h2>
                <label className="switch">
                  <input
                    type="checkbox"
                    checked={config.hourlySystemEnabled}
                    onChange={(e) => setConfig({...config, hourlySystemEnabled: e.target.checked})}
                  />
                  <span className="slider"></span>
                </label>
              </div>
              {config.hourlyPackages.map((pkg, index) => (
                <div key={index} className="package-config">
                  <div className="config-header">
                    <h4>باقة {pkg.hours} ساعات</h4>
                    <label className="switch">
                      <input
                        type="checkbox"
                        checked={pkg.enabled}
                        onChange={(e) => handleHourlyPackageUpdate(index, { enabled: e.target.checked })}
                      />
                      <span className="slider"></span>
                    </label>
                  </div>
                  
                  <div className="config-fields horizontal">
                    <div className="field-group">
                      <label>عدد الساعات</label>
                      <input
                        type="number"
                        value={pkg.hours}
                        onChange={(e) => handleHourlyPackageUpdate(index, { hours: parseInt(e.target.value) || 0 })}
                        min="1"
                      />
                    </div>
                    
                    <div className="field-group">
                      <label>
                        السعر الحالي
                        <RiyalSymbol size={16} className="label-riyal" />
                      </label>
                      <input
                        type="number"
                        value={pkg.price}
                        onChange={(e) => handleHourlyPackageUpdate(index, { price: parseInt(e.target.value) || 0 })}
                        min="0"
                      />
                    </div>
                  </div>
                  
                  <div className="discount-section">
                    <div className="config-fields horizontal">
                      <div className="field-group">
                        <label>
                          السعر القديم (قبل الخصم)
                          <RiyalSymbol size={16} className="label-riyal" />
                        </label>
                        <input
                          type="number"
                          value={pkg.originalPrice || ''}
                          onChange={(e) => handleHourlyPackageUpdate(index, { originalPrice: e.target.value ? parseInt(e.target.value) : undefined })}
                          min="0"
                          placeholder="اختياري"
                        />
                      </div>
                      
                      <div className="field-group">
                        <label>نسبة الخصم %</label>
                        <input
                          type="number"
                          value={pkg.discount || ''}
                          onChange={(e) => handleHourlyPackageUpdate(index, { discount: e.target.value ? parseInt(e.target.value) : undefined })}
                          min="0"
                          max="100"
                          placeholder="اختياري"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="pricing-group">
              <div className="system-header">
                <h2>
                  نظام التذاكر
                  <span className="info-badge">حسب عدد الصور</span>
                </h2>
                <label className="switch">
                  <input
                    type="checkbox"
                    checked={config.ticketSystemEnabled}
                    onChange={(e) => setConfig({...config, ticketSystemEnabled: e.target.checked})}
                  />
                  <span className="slider"></span>
                </label>
              </div>
              <div className="package-config">
                <div className="config-header">
                  <h4>إعدادات نظام التذاكر</h4>
                </div>
                
                <div className="config-fields">
                  <div className="field-group">
                    <label>
                      رسوم التركيب الحالية
                      <RiyalSymbol size={16} className="label-riyal" />
                    </label>
                    <input
                      type="number"
                      value={config.ticketSystem.installationFee}
                      onChange={(e) => handleTicketSystemUpdate({ installationFee: parseInt(e.target.value) || 0 })}
                      min="0"
                    />
                  </div>
                  
                  <div className="field-group">
                    <label>
                      رسوم التركيب القديمة (قبل الخصم)
                      <RiyalSymbol size={16} className="label-riyal" />
                    </label>
                    <input
                      type="number"
                      value={config.ticketSystem.originalInstallationFee || ''}
                      onChange={(e) => handleTicketSystemUpdate({ originalInstallationFee: e.target.value ? parseInt(e.target.value) : undefined })}
                      min="0"
                      placeholder="اختياري"
                    />
                  </div>
                  
                  <div className="field-group">
                    <label>نسبة خصم رسوم التركيب %</label>
                    <input
                      type="number"
                      value={config.ticketSystem.installationDiscount || ''}
                      onChange={(e) => handleTicketSystemUpdate({ installationDiscount: e.target.value ? parseInt(e.target.value) : undefined })}
                      min="0"
                      max="100"
                      placeholder="اختياري"
                    />
                  </div>
                  
                  <div className="field-group">
                    <label>عدد التذاكر المجانية</label>
                    <input
                      type="number"
                      value={config.ticketSystem.freeTickets}
                      onChange={(e) => handleTicketSystemUpdate({ freeTickets: parseInt(e.target.value) || 0 })}
                      min="0"
                    />
                  </div>
                  
                  <div className="field-group">
                    <label>
                      سعر التذكرة الإضافية الحالي
                      <RiyalSymbol size={16} className="label-riyal" />
                    </label>
                    <input
                      type="number"
                      value={config.ticketSystem.ticketPrice}
                      onChange={(e) => handleTicketSystemUpdate({ ticketPrice: parseInt(e.target.value) || 0 })}
                      min="0"
                    />
                  </div>
                  
                  <div className="field-group">
                    <label>
                      سعر التذكرة القديم (قبل الخصم)
                      <RiyalSymbol size={16} className="label-riyal" />
                    </label>
                    <input
                      type="number"
                      value={config.ticketSystem.originalTicketPrice || ''}
                      onChange={(e) => handleTicketSystemUpdate({ originalTicketPrice: e.target.value ? parseInt(e.target.value) : undefined })}
                      min="0"
                      placeholder="اختياري"
                    />
                  </div>
                  
                  <div className="field-group">
                    <label>نسبة خصم سعر التذاكر %</label>
                    <input
                      type="number"
                      value={config.ticketSystem.ticketDiscount || ''}
                      onChange={(e) => handleTicketSystemUpdate({ ticketDiscount: e.target.value ? parseInt(e.target.value) : undefined })}
                      min="0"
                      max="100"
                      placeholder="اختياري"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Features Tab */}
        {activeTab === 'features' && (
          <div className="features-section">
            <div className="section-description">
              <strong>✨ المميزات:</strong> اكتب المميزات التي تقدمها لكل نظام. ستظهر هذه المميزات للعملاء عند اختيار الباقة.
            </div>
            <div className="features-group">
              <h2>مميزات نظام الساعات</h2>
              {config.features.hourlyFeatures.map((feature, index) => (
                <div key={index} className="feature-item">
                  <input
                    type="text"
                    value={feature}
                    onChange={(e) => updateFeature('hourlyFeatures', index, e.target.value)}
                  />
                  <button 
                    className="remove-btn"
                    onClick={() => removeFeature('hourlyFeatures', index)}
                  >
                    حذف
                  </button>
                </div>
              ))}
              <button 
                className="add-feature-btn"
                onClick={() => addFeature('hourlyFeatures')}
              >
                + إضافة ميزة
              </button>
            </div>

            <div className="features-group">
              <h2>مميزات نظام التذاكر</h2>
              {config.features.ticketFeatures.map((feature, index) => (
                <div key={index} className="feature-item">
                  <input
                    type="text"
                    value={feature}
                    onChange={(e) => updateFeature('ticketFeatures', index, e.target.value)}
                  />
                  <button 
                    className="remove-btn"
                    onClick={() => removeFeature('ticketFeatures', index)}
                  >
                    حذف
                  </button>
                </div>
              ))}
              <button 
                className="add-feature-btn"
                onClick={() => addFeature('ticketFeatures')}
              >
                + إضافة ميزة
              </button>
            </div>
          </div>
        )}

        {/* Contact Tab */}
        {activeTab === 'contact' && (
          <div className="contact-section">
            <h2>معلومات الاتصال</h2>
            <div className="section-description">
              <strong>📞 التواصل:</strong> هذه المعلومات ستظهر في أسفل الموقع وفي رسائل البريد الإلكتروني المرسلة للعملاء.
            </div>
            <div className="contact-config">
              <div className="config-fields">
                <div className="field-group">
                  <label>📧 البريد الإلكتروني</label>
                  <input
                    type="email"
                    value={config.contactInfo?.email || ''}
                    onChange={(e) => handleContactUpdate({ email: e.target.value })}
                    placeholder="info@example.com"
                  />
                </div>
                
                <div className="field-group">
                  <label>📱 رقم الواتساب</label>
                  <input
                    type="text"
                    value={config.contactInfo?.whatsapp || ''}
                    onChange={(e) => handleContactUpdate({ whatsapp: e.target.value })}
                    placeholder="+966501234567"
                    dir="ltr"
                  />
                </div>
                
                <div className="field-group">
                  <label>📸 حساب الانستقرام</label>
                  <input
                    type="text"
                    value={config.contactInfo?.instagram || ''}
                    onChange={(e) => handleContactUpdate({ instagram: e.target.value })}
                    placeholder="@username"
                    dir="ltr"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="admin-actions">
          <motion.button 
            className={`save-btn ${hasUnsavedChanges ? 'has-changes' : ''} ${isSaving ? 'saving' : ''}`}
            onClick={handleSave}
            disabled={isSaving}
            whileHover={!isSaving ? { scale: 1.02 } : {}}
            whileTap={!isSaving ? { scale: 0.98 } : {}}
          >
            <span className="btn-content">
              {isSaving && (
                <motion.div 
                  className="spinner"
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                >
                  ⚙️
                </motion.div>
              )}
              <span className="btn-text">
                {isSaving ? 'جاري الحفظ...' : hasUnsavedChanges ? '💾 حفظ التغييرات' : '✅ محفوظ'}
              </span>
            </span>
          </motion.button>
          <motion.button 
            className="reset-btn"
            onClick={handleReset}
            disabled={isSaving}
            whileHover={!isSaving ? { scale: 1.02 } : {}}
            whileTap={!isSaving ? { scale: 0.98 } : {}}
          >
            <span className="btn-content">
              <span className="btn-text">🔄 إعادة تعيين الإعدادات</span>
            </span>
          </motion.button>
        </div>

        <AnimatePresence>
          {showSuccess && (
            <motion.div 
              className="success-message"
              initial={{ opacity: 0, y: -50, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -50, scale: 0.9 }}
              transition={{ duration: 0.3 }}
            >
              <div className="success-content">
                <span className="success-icon">✅</span>
                <span className="success-text">{successMessage}</span>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {hasUnsavedChanges && (
          <motion.div 
            className="unsaved-warning"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <span className="warning-icon">⚠️</span>
            <span>لديك تغييرات غير محفوظة</span>
          </motion.div>
        )}

      </motion.div>
    </AdminLayout>
  );
}

export default PagesManagement;